import {
  Table,
  TableBody,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  Typography,
  Button,
} from '@mui/material'
import FullHeightFlexContainer from 'components/ui/FullHeightFlexContainer'
import { DocumentOrderForm } from 'entities/DocumentsOrderForm'
import getOrderFormFields from 'entities/getOrderFormFields'
import { useMemo } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'

import { ITEM_TEMPLATE, ITEM_KEYS } from './common'
import ItemRow from './ItemRow'

interface Props {
  docDetail: DocumentOrderForm
}

function Items({ docDetail }: Props) {
  const { config } = docDetail
  const visibleItems = ITEM_KEYS.filter((key) => config.itemLines[key]?.display || false)
  const isChecking = docDetail.isChecking
  const currentFields = useMemo(() => getOrderFormFields(docDetail), [docDetail])

  const { control } = useFormContext()
  const { fields, remove, insert } = useFieldArray({
    control,
    name: 'itemLines',
    keyName: 'editId',
  })

  const getFieldsConfig = (id: string) => {
    // item line can be added which can't have field data
    // line[0] is id
    const rtn = currentFields?.itemLines?.find((line) => (line[0] as any) === id)
    return rtn ? rtn.slice(1) : currentFields?.itemDefaultField
  }

  const handleAdd = (idx) => {
    insert(idx, { ...ITEM_TEMPLATE, order: 0 })
  }

  const labelCells = visibleItems.map((key) => {
    if (
      config?.itemLines[key]?.display === false ||
      (isChecking && config.itemLines?.[key]?.verify === false)
    ) {
      return null
    }
    if (key === 'dateN') return null
    return (
      <TableCell key={key} sx={{ whiteSpace: 'noWrap' }}>
        {docDetail.getLabel('itemLines', key)}
        {key === 'name---' && ( // not used currently
          <Typography display="inline" color="error">
            {' '}
            *
          </Typography>
        )}
      </TableCell>
    )
  })

  const empty = (
    <>
      <TableCell />
      <TableCell colSpan={visibleItems.length}>
        <Button
          disabled={!docDetail.canEdit()}
          size="small"
          color="primary"
          variant="outlined"
          onClick={() => handleAdd(0)}
        >
          品目を追加
        </Button>
      </TableCell>
    </>
  )

  // TODO: line as any - due to the keyName of useFieldArray
  const rows = fields?.map((line, idx) => (
    <ItemRow
      key={line.editId}
      editIdx={idx}
      defaultValues={line}
      fieldsConfig={getFieldsConfig((line as any).id)}
      id={(line as any).id}
      editId={line.editId}
      order={(line as any).order}
      onDel={() => remove(idx)}
      onAdd={() => handleAdd(idx + 1)}
      canEdit={docDetail.canEdit()}
      isChecking={isChecking}
      config={config}
    />
  ))

  return (
    <FullHeightFlexContainer>
      <TableContainer style={{ height: '100%' }}>
        <Table stickyHeader size="medium" aria-label="item fields table">
          <TableHead data-testid="table-head-item-edit">
            <TableRow>
              <TableCell />
              {labelCells}
            </TableRow>
          </TableHead>
          <TableBody data-testid="table-body-item-edit">{rows.length ? rows : empty}</TableBody>
        </Table>
      </TableContainer>
    </FullHeightFlexContainer>
  )
}

export default Items
